import { PDFFont, PDFPage, rgb } from "pdf-lib"
import { pageHeight as a4PageHeight } from "./constants"

/**
 * Attributed-text layout engine ported from the iOS PDF generator.
 * iOS sources of truth:
 * - WorkPDFGenerator.drawText(text:/attributedText:) — measurement + vertical alignment
 * - NSAttributedString+Extension.cut(seperateBy:) — piece splitting (pieces retain LEADING separator)
 * - WorkPDFGenerator.drawImageInfoPage / drawWCImageInfoPage — word-level pagination loops
 */

export type TRgb = [number, number, number]

export const black: TRgb = [0, 0, 0]
export const gray: TRgb = [0.5, 0.5, 0.5] // UIColor.gray
export const white: TRgb = [1, 1, 1]

export interface ITextStyle {
	font: PDFFont
	size: number
	/** paragraph lineSpacing (gap below each line of this style) */
	lineSpacing: number
	color?: TRgb
	underline?: boolean
	/** parity with iOS section-header detection (Lato-Regular 10 on info pages) */
	isSectionHeader?: boolean
	align?: "left" | "center"
}

export interface IRun {
	text: string
	style: ITextStyle
}

export function runsText(runs: IRun[]): string {
	return runs.map((r) => r.text).join("")
}

export function runsFromText(text: string, style: ITextStyle): IRun[] {
	return text === "" ? [] : [{ text, style }]
}

/** Slice a run list by absolute character range [start, end). */
function sliceRuns(runs: IRun[], start: number, end: number): IRun[] {
	const out: IRun[] = []
	let pos = 0
	runs.forEach((run) => {
		const runStart = pos
		const runEnd = pos + run.text.length
		pos = runEnd
		const from = Math.max(start, runStart)
		const to = Math.min(end, runEnd)
		if (from < to) {
			out.push({ text: run.text.slice(from - runStart, to - runStart), style: run.style })
		}
	})
	return out
}

/**
 * Port of iOS NSAttributedString.cut(seperateBy:) — split into components,
 * where every component AFTER the first keeps its LEADING separator, so plain
 * concatenation of all pieces reproduces the original string exactly.
 */
export function cutRuns(runs: IRun[], separator: string): IRun[][] {
	const input = runsText(runs)
	const separated = input.split(separator)
	const output: IRun[][] = []
	let start = 0
	separated.forEach((sub, i) => {
		if (i === 0) {
			output.push(sliceRuns(runs, 0, sub.length))
			start += sub.length + separator.length
		} else {
			const from = Math.max(0, start - separator.length)
			output.push(sliceRuns(runs, from, from + sub.length + separator.length))
			start += sub.length + separator.length
		}
	})
	return output
}

/** iOS: descAttrS.cut(seperateBy: "\n").flatMap { $0.cut(seperateBy: " ") } */
export function splitIntoPieces(runs: IRun[]): IRun[][] {
	const byLine = cutRuns(runs, "\n")
	const pieces: IRun[][] = []
	byLine.forEach((line) => {
		cutRuns(line, " ").forEach((piece) => pieces.push(piece))
	})
	return pieces
}

// ============================== wrapping ==============================

interface ISegment {
	text: string
	style: ITextStyle
	width: number
}

export interface ILine {
	segments: ISegment[]
	width: number
	/** max ascent among segments (baseline offset from line top) */
	ascent: number
	/** full line box height */
	height: number
	/** gap below this line (paragraph lineSpacing of the line's style) */
	lineSpacing: number
	align: "left" | "center"
}

// Memoized text measurement — pagination re-measures the same words many
// times (the fit test grows the page content piece by piece), and fontkit
// glyph layout dominates the cost without a cache.
const widthCache = new WeakMap<PDFFont, Map<string, number>>()
const metricsCache = new WeakMap<PDFFont, Map<number, { ascent: number; height: number }>>()

function textWidth(style: ITextStyle, text: string): number {
	if (text === "") {
		return 0
	}
	let perFont = widthCache.get(style.font)
	if (!perFont) {
		perFont = new Map()
		widthCache.set(style.font, perFont)
	}
	const key = `${style.size}|${text}`
	const cached = perFont.get(key)
	if (cached !== undefined) {
		return cached
	}
	const width = style.font.widthOfTextAtSize(text, style.size)
	perFont.set(key, width)
	return width
}

function lineMetrics(style: ITextStyle): { ascent: number; height: number } {
	let perFont = metricsCache.get(style.font)
	if (!perFont) {
		perFont = new Map()
		metricsCache.set(style.font, perFont)
	}
	const cached = perFont.get(style.size)
	if (cached) {
		return cached
	}
	const metrics = {
		ascent: style.font.heightAtSize(style.size, { descender: false }),
		height: style.font.heightAtSize(style.size),
	}
	perFont.set(style.size, metrics)
	return metrics
}

interface IToken {
	text: string
	style: ITextStyle
	kind: "word" | "space" | "newline"
}

function tokenize(runs: IRun[]): IToken[] {
	const tokens: IToken[] = []
	runs.forEach((run) => {
		let buf = ""
		const flush = () => {
			if (buf !== "") {
				tokens.push({ text: buf, style: run.style, kind: buf[0] === " " ? "space" : "word" })
				buf = ""
			}
		}
		for (const ch of run.text) {
			if (ch === "\n") {
				flush()
				tokens.push({ text: "\n", style: run.style, kind: "newline" })
			} else if (ch === " ") {
				if (buf !== "" && buf[0] !== " ") {
					flush()
				}
				buf += ch
			} else {
				if (buf !== "" && buf[0] === " ") {
					flush()
				}
				buf += ch
			}
		}
		flush()
	})
	return tokens
}

/** Word-wrap runs into lines (byWordWrapping parity, incl. mid-word break for overlong words). */
export function wrapRuns(runs: IRun[], maxWidth: number): ILine[] {
	const lines: ILine[] = []
	let segments: ISegment[] = []
	let lastStyle: ITextStyle | null = null

	const trimmedWidth = (segs: ISegment[]): number => {
		// trailing spaces do not count toward the visual line width
		let total = 0
		let pendingSpace = 0
		segs.forEach((s) => {
			if (s.text.trim() === "") {
				pendingSpace += s.width
			} else {
				total += pendingSpace + s.width
				pendingSpace = 0
			}
		})
		return total
	}

	const finishLine = (breakStyle: ITextStyle | null) => {
		const style = segments[0]?.style ?? breakStyle ?? lastStyle
		let ascent = 0
		let height = 0
		if (segments.length === 0) {
			// empty line (blank paragraph) takes the height of the breaking style
			const m = lineMetrics(style as ITextStyle)
			ascent = m.ascent
			height = m.height
		} else {
			segments.forEach((s) => {
				const m = lineMetrics(s.style)
				ascent = Math.max(ascent, m.ascent)
				height = Math.max(height, m.height)
			})
		}
		lines.push({
			segments,
			width: trimmedWidth(segments),
			ascent,
			height,
			lineSpacing: (style as ITextStyle).lineSpacing,
			align: (style as ITextStyle).align ?? "center",
		})
		segments = []
	}

	const currentWidth = (): number => segments.reduce((acc, s) => acc + s.width, 0)

	const pushSegment = (text: string, style: ITextStyle) => {
		segments.push({ text, style, width: textWidth(style, text) })
		lastStyle = style
	}

	const tokens = tokenize(runs)
	tokens.forEach((token) => {
		if (token.kind === "newline") {
			finishLine(token.style)
			return
		}
		if (token.kind === "space") {
			pushSegment(token.text, token.style)
			return
		}
		// word
		const wordWidth = textWidth(token.style, token.text)
		if (trimmedWidth(segments) > 0 && trimmedWidth(segments) + spacePendingWidth(segments) + wordWidth > maxWidth) {
			finishLine(token.style)
		}
		if (wordWidth > maxWidth) {
			// mid-word break for words wider than the whole line
			let rest = token.text
			while (rest !== "") {
				let take = rest.length
				while (take > 1 && textWidth(token.style, rest.slice(0, take)) + currentWidth() > maxWidth) {
					take -= 1
				}
				pushSegment(rest.slice(0, take), token.style)
				rest = rest.slice(take)
				if (rest !== "") {
					finishLine(token.style)
				}
			}
			return
		}
		pushSegment(token.text, token.style)
	})

	if (segments.length > 0 || lines.length === 0) {
		if (runsText(runs) !== "") {
			finishLine(lastStyle)
		}
	}

	return lines
}

function spacePendingWidth(segments: ISegment[]): number {
	// width of trailing spaces (they precede the next word)
	let width = 0
	for (let i = segments.length - 1; i >= 0; i--) {
		if (segments[i].text.trim() === "") {
			width += segments[i].width
		} else {
			break
		}
	}
	return width
}

/** Total layout height: sum of line heights + each line's trailing lineSpacing except the last. */
export function measureHeight(runs: IRun[], maxWidth: number): number {
	const lines = wrapRuns(runs, maxWidth)
	if (lines.length === 0) {
		return 0
	}
	let total = 0
	lines.forEach((line, i) => {
		total += line.height
		if (i < lines.length - 1) {
			total += line.lineSpacing
		}
	})
	return total
}

// ============================== drawing ==============================

export interface ITopRect {
	x: number
	/** y measured from the TOP of the page (iOS coordinates) */
	yTop: number
	width: number
	height: number
}

export type TVerticalAlign = "top" | "center" | "bottom"

/**
 * Port of iOS WorkPDFGenerator.drawText(attributedText:alignVertical:rect:).
 * Returns the final rect (top-down coords) for layout chaining.
 */
export function drawRuns(
	page: PDFPage,
	runs: IRun[],
	rect: ITopRect,
	vAlign: TVerticalAlign = "center",
	pageHeight: number = a4PageHeight
): ITopRect {
	const lines = wrapRuns(runs, rect.width)

	let textHeight = 0
	lines.forEach((line, i) => {
		textHeight += line.height
		if (i < lines.length - 1) {
			textHeight += line.lineSpacing
		}
	})
	textHeight = Math.ceil(textHeight)

	let paddingY = 0
	if (vAlign === "bottom") {
		paddingY = rect.height - textHeight
	} else if (vAlign === "center") {
		paddingY = (rect.height - textHeight) / 2
	}
	paddingY = Math.max(0, paddingY)

	let cursorTop = rect.yTop + paddingY
	lines.forEach((line) => {
		let x = rect.x
		if (line.align === "center") {
			x = rect.x + (rect.width - line.width) / 2
		}
		const baselineTop = cursorTop + line.ascent
		const y = pageHeight - baselineTop
		line.segments.forEach((segment) => {
			if (segment.text.trim() !== "") {
				page.drawText(segment.text, {
					x,
					y,
					size: segment.style.size,
					font: segment.style.font,
					color: rgb(...((segment.style.color ?? black) as [number, number, number])),
				})
				if (segment.style.underline) {
					const underlineY = y - 1.5
					page.drawLine({
						start: { x, y: underlineY },
						end: { x: x + segment.width, y: underlineY },
						thickness: Math.max(0.5, segment.style.size * 0.05),
						color: rgb(...((segment.style.color ?? black) as [number, number, number])),
					})
				}
			}
			x += segment.width
		})
		cursorTop += line.height + line.lineSpacing
	})

	return { x: rect.x, yTop: rect.yTop + paddingY, width: rect.width, height: textHeight }
}

// ============================== pagination ==============================

export interface IPaginationRect {
	width: number
	height: number
}

/**
 * Incremental wrap meter: measures text height while runs are appended,
 * with O(1) cloning — used by pagination so the fit test does not re-wrap
 * the whole accumulated page for every piece. Mirrors wrapRuns semantics.
 */
interface IWrapMeterState {
	// finished-lines contribution and the pending gap before the next line
	totalHeight: number
	pendingSpacing: number
	hasFinishedLines: boolean
	// current (unfinished) line
	lineWidth: number // trimmed width (excludes trailing spaces)
	trailingSpaceWidth: number
	lineHeight: number
	lineStarted: boolean
	lineSpacing: number // spacing of the current line (first segment's style)
	haveFirstStyle: boolean
}

class WrapMeter {
	private s: IWrapMeterState
	constructor(private maxWidth: number, state?: IWrapMeterState) {
		this.s = state ?? {
			totalHeight: 0,
			pendingSpacing: 0,
			hasFinishedLines: false,
			lineWidth: 0,
			trailingSpaceWidth: 0,
			lineHeight: 0,
			lineStarted: false,
			lineSpacing: 0,
			haveFirstStyle: false,
		}
	}

	clone(): WrapMeter {
		return new WrapMeter(this.maxWidth, { ...this.s })
	}

	private finishLine(breakStyle: ITextStyle): void {
		const s = this.s
		const height = s.lineStarted ? s.lineHeight : lineMetrics(breakStyle).height
		const spacing = s.haveFirstStyle ? s.lineSpacing : breakStyle.lineSpacing
		if (s.hasFinishedLines) {
			s.totalHeight += s.pendingSpacing
		}
		s.totalHeight += height
		s.pendingSpacing = spacing
		s.hasFinishedLines = true
		s.lineWidth = 0
		s.trailingSpaceWidth = 0
		s.lineHeight = 0
		s.lineStarted = false
		s.lineSpacing = 0
		s.haveFirstStyle = false
	}

	private touchStyle(style: ITextStyle): void {
		const s = this.s
		if (!s.haveFirstStyle) {
			s.lineSpacing = style.lineSpacing
			s.haveFirstStyle = true
		}
		s.lineHeight = Math.max(s.lineHeight, lineMetrics(style).height)
		s.lineStarted = true
	}

	add(runs: IRun[]): void {
		const s = this.s
		tokenize(runs).forEach((token) => {
			if (token.kind === "newline") {
				this.finishLine(token.style)
				return
			}
			if (token.kind === "space") {
				s.trailingSpaceWidth += textWidth(token.style, token.text)
				this.touchStyle(token.style)
				return
			}
			const wordWidth = textWidth(token.style, token.text)
			if (s.lineWidth > 0 && s.lineWidth + s.trailingSpaceWidth + wordWidth > this.maxWidth) {
				this.finishLine(token.style)
			}
			if (wordWidth > this.maxWidth) {
				// mid-word break parity with wrapRuns
				let rest = token.text
				while (rest !== "") {
					let take = rest.length
					while (
						take > 1 &&
						textWidth(token.style, rest.slice(0, take)) + s.lineWidth + s.trailingSpaceWidth > this.maxWidth
					) {
						take -= 1
					}
					s.lineWidth += s.trailingSpaceWidth + textWidth(token.style, rest.slice(0, take))
					s.trailingSpaceWidth = 0
					this.touchStyle(token.style)
					rest = rest.slice(take)
					if (rest !== "") {
						this.finishLine(token.style)
					}
				}
				return
			}
			s.lineWidth += s.trailingSpaceWidth + wordWidth
			s.trailingSpaceWidth = 0
			this.touchStyle(token.style)
		})
	}

	height(): number {
		const s = this.s
		if (!s.lineStarted) {
			return s.totalHeight
		}
		return s.totalHeight + (s.hasFinishedLines ? s.pendingSpacing : 0) + s.lineHeight
	}
}

/**
 * Port of the iOS pagination loops (drawImageInfoPage / drawWCImageInfoPage).
 * - `rectFor(pageNumber)` returns the content-area size for that page (1-based).
 * - `headerWidowRule` doubles a section-header piece in the fit test so a header
 *   never sits alone at the bottom of a page (general info pages only).
 * - `emit(pageRuns, pageNumber)` is called for each page's text content, in order.
 */
export function paginateRuns(
	runs: IRun[],
	rectFor: (pageNumber: number) => IPaginationRect,
	headerWidowRule: boolean,
	emit: (pageRuns: IRun[], pageNumber: number) => void
): void {
	const pieces = splitIntoPieces(runs)
	pieces.push([]) // terminal empty piece flushes the final page

	let thisPage: IRun[] = []
	let pagesCount = 1
	let meter = new WrapMeter(rectFor(1).width)
	let thisPageText = ""

	pieces.forEach((piece, pieceIndex) => {
		const pieceText = runsText(piece)
		const isHeader = pieceText === "" ? false : headerWidowRule && Boolean(piece[0].style.isSectionHeader)

		const rect = rectFor(pagesCount)

		let shouldDrawPage: boolean
		let committed: WrapMeter | null = null
		if (pieceIndex === pieces.length - 1) {
			shouldDrawPage = thisPageText !== ""
		} else {
			const test = meter.clone()
			test.add(piece)
			committed = test
			if (isHeader) {
				const doubled = test.clone()
				doubled.add(piece)
				shouldDrawPage = doubled.height() > rect.height
			} else {
				shouldDrawPage = test.height() > rect.height
			}
		}

		if (shouldDrawPage && thisPageText.replace(/\n/g, "") !== "") {
			emit(thisPage, pagesCount)
			thisPage = []
			thisPageText = ""
			pagesCount += 1
			meter = new WrapMeter(rectFor(pagesCount).width)
			meter.add(piece)
		} else if (committed) {
			meter = committed
		}

		piece.forEach((run) => thisPage.push(run))
		thisPageText += pieceText
	})
}
